import { Component } from '@theme/component';
import { VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { getViewParameterValue } from '@theme/utilities';

/**
 * @typedef {object} GroupEntry
 * @property {number} id
 * @property {string} handle
 * @property {string} url
 * @property {string} option1
 * @property {string} option2
 * @property {number} variantId
 * @property {boolean} available
 * @property {number} price
 * @property {string} price_money
 */

class ProductGroupPicker extends Component {
  /** @type {AbortController | undefined} */
  #abort;

  /** @type {GroupEntry[]} */
  #entries = [];

  /** @type {Set<string>} */
  #prefetchedPaths = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.#sortGroupedOptionLabels();
    this.addEventListener('change', this.#onChange);
    this.addEventListener('pointerenter', this.#onPointerEnterPrefetch, true);
    this.#parseEntries();
    this.#syncOption1Availability();
    this.#syncDisabledPackOptions();
    this.#ensureValidPair();
    this.#syncPackPrices();
    this.#syncOption1Availability();
    this.#syncDisabledPackOptions();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  /**
   * Match PDP expectations: Size = Small → Medium → Large → Monster; Pack = numeric (Pack of 1, 2, …).
   */
  #sortGroupedOptionLabels() {
    /** @type {string[]} */
    const sizeRankOrder = ['small', 'medium', 'large', 'monster'];
    const sizeRank = (/** @type {string} */ t) => {
      const i = sizeRankOrder.indexOf(t.toLowerCase());
      return i === -1 ? sizeRankOrder.length : i;
    };

    /**
     * @param {string} text
     * @returns {number}
     */
    const extractPackNumber = (text) => {
      const match = text.match(/\d+(?:\.\d+)?/);
      return match ? Number(match[0]) : Number.NaN;
    };

    /**
     * @param {HTMLLabelElement} label
     * @returns {string}
     */
    const labelText = (label) => {
      const explicit = label.querySelector('[data-key="variant-option-text"]')?.textContent;
      return ((explicit ?? label.textContent) || '').trim();
    };

    for (const axis of ['1', '2']) {
      const fieldset = this.querySelector(`fieldset[data-product-group-axis="${axis}"]`);
      if (!fieldset) continue;

      const labels = Array.from(fieldset.querySelectorAll(':scope > label.variant-option__button-label'));
      if (labels.length < 2) continue;

      labels.sort((a, b) => {
        const aRaw = labelText(a);
        const bRaw = labelText(b);
        if (axis === '1') {
          const ra = sizeRank(aRaw);
          const rb = sizeRank(bRaw);
          if (ra !== rb) return ra - rb;
        } else {
          const na = extractPackNumber(aRaw);
          const nb = extractPackNumber(bRaw);
          if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
        }
        return aRaw.localeCompare(bRaw, undefined, { numeric: true, sensitivity: 'base' });
      });

      const firstStrikethrough = fieldset.querySelector(':scope > .strikethrough-variant');
      for (const label of labels) {
        if (firstStrikethrough) {
          fieldset.insertBefore(label, firstStrikethrough);
        } else {
          fieldset.appendChild(label);
        }
      }
    }
  }

  #parseEntries() {
    const el = this.querySelector('script[data-product-group-entries]');
    if (!el?.textContent) return;
    try {
      const raw = JSON.parse(el.textContent.trim());
      this.#entries = Array.isArray(raw) ? raw : [];
    } catch {
      this.#entries = [];
    }
  }

  /** @returns {HTMLInputElement | null} */
  #checkedOpt1() {
    return this.querySelector('fieldset[data-product-group-axis="1"] input[type="radio"]:checked');
  }

  /** @returns {HTMLInputElement | null} */
  #checkedOpt2() {
    return this.querySelector('fieldset[data-product-group-axis="2"] input[type="radio"]:checked');
  }

  /**
   * @param {unknown} s
   * @returns {string}
   */
  #norm(s) {
    return (s ?? '').toString().trim();
  }

  /**
   * @param {string} o1
   * @param {string} o2
   * @returns {GroupEntry | undefined}
   */
  #findEntry(o1, o2) {
    const a = this.#norm(o1);
    const b = this.#norm(o2);
    return this.#entries.find((e) => this.#norm(e.option1) === a && this.#norm(e.option2) === b);
  }

  #ensureValidPair() {
    const o1 = this.#checkedOpt1()?.value ?? '';
    let o2 = this.#checkedOpt2()?.value ?? '';
    let entry = this.#findEntry(o1, o2);
    if (entry) return entry;

    if (o1) {
      const fieldset2 = this.querySelector('fieldset[data-product-group-axis="2"]');
      if (!fieldset2) return entry;

      let fallbackInput = null;
      let fallbackEntry = undefined;

      for (const r of fieldset2.querySelectorAll('input[type="radio"]')) {
        if (!(r instanceof HTMLInputElement)) continue;
        if (r.closest('label')?.hidden) continue;
        const candidate = this.#findEntry(o1, r.value);
        if (!candidate) continue;
        if (!r.disabled && candidate.available) {
          fallbackInput = r;
          fallbackEntry = candidate;
          break;
        }
        if (!fallbackInput) {
          fallbackInput = r;
          fallbackEntry = candidate;
        }
      }

      if (fallbackInput && fallbackEntry) {
        fallbackInput.checked = true;
        o2 = fallbackEntry.option2;
        entry = fallbackEntry;
      }
    }
    return entry;
  }

  #syncPackPrices() {
    const o1 = this.#norm(this.#checkedOpt1()?.value);
    const fieldset2 = this.querySelector('fieldset[data-product-group-axis="2"]');
    if (!fieldset2) return;

    for (const label of fieldset2.querySelectorAll('label.variant-option__button-label')) {
      const input = label.querySelector('input[type="radio"]');
      const sub = label.querySelector('[data-fake-pack-price]');
      if (!(input instanceof HTMLInputElement) || !sub) continue;
      const o2 = this.#norm(input.value);
      const entry = o1 ? this.#findEntry(o1, o2) : undefined;
      if (entry) {
        sub.textContent = entry.price_money;
        if (sub instanceof HTMLElement) sub.hidden = false;
      } else {
        sub.textContent = '';
        if (sub instanceof HTMLElement) sub.hidden = true;
      }
    }
  }

  #syncDisabledPackOptions() {
    const o1 = this.#norm(this.#checkedOpt1()?.value);
    const fieldset2 = this.querySelector('fieldset[data-product-group-axis="2"]');
    if (!fieldset2) return;

    for (const labelEl of fieldset2.querySelectorAll('label.variant-option__button-label')) {
      if (!(labelEl instanceof HTMLElement)) continue;
      const input = labelEl.querySelector('input[type="radio"]');
      if (!(input instanceof HTMLInputElement)) continue;
      const o2 = this.#norm(input.value);
      const entry = o1 ? this.#findEntry(o1, o2) : undefined;
      const hasMatch = Boolean(entry);
      const isAvailable = Boolean(entry?.available);

      // Show only packs related to the selected size.
      // Use explicit display toggle for stronger compatibility across theme styles.
      labelEl.hidden = !hasMatch;
      labelEl.style.display = hasMatch ? '' : 'none';

      // Keep related packs clickable even when unavailable so users can switch product/pack.
      input.disabled = !hasMatch;
      input.setAttribute('aria-disabled', !hasMatch ? 'true' : 'false');
      labelEl.classList.toggle('is-unavailable', hasMatch && !isAvailable);
      this.#toggleUnavailableStrikethrough(labelEl, hasMatch && !isAvailable);

      if (!hasMatch && input.checked) {
        input.checked = false;
      }
    }
  }

  /**
   * Keep grouped-picker unavailable UI aligned with the native variant picker
   * by rendering/removing the same strike SVG dynamically.
   * @param {HTMLElement} labelEl
   * @param {boolean} show
   */
  #toggleUnavailableStrikethrough(labelEl, show) {
    const selector = '.variant-option__strikethrough[data-group-generated="true"]';
    const existing = labelEl.querySelector(selector);

    if (!show) {
      existing?.remove();
      return;
    }

    if (existing) return;

    const NS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('viewBox', '0 0 100 46');
    svg.setAttribute('preserveAspectRatio', 'xMidYMid slice');
    svg.setAttribute('class', 'variant-option__strikethrough');
    svg.setAttribute('data-group-generated', 'true');

    const lineA = document.createElementNS(NS, 'line');
    lineA.setAttribute('x1', '100');
    lineA.setAttribute('y1', '0');
    lineA.setAttribute('x2', '0');
    lineA.setAttribute('y2', '46');
    lineA.setAttribute('vector-effect', 'non-scaling-stroke');

    const lineB = document.createElementNS(NS, 'line');
    lineB.setAttribute('x1', '100');
    lineB.setAttribute('y1', '0');
    lineB.setAttribute('x2', '0');
    lineB.setAttribute('y2', '46');
    lineB.setAttribute('vector-effect', 'non-scaling-stroke');

    svg.append(lineA, lineB);
    labelEl.appendChild(svg);
  }

  #syncOption1Availability() {
    const o2 = this.#norm(this.#checkedOpt2()?.value);
    const fieldset1 = this.querySelector('fieldset[data-product-group-axis="1"]');
    if (!fieldset1) return;

    for (const labelEl of fieldset1.querySelectorAll('label.variant-option__button-label')) {
      if (!(labelEl instanceof HTMLElement)) continue;
      const input = labelEl.querySelector('input[type="radio"]');
      if (!(input instanceof HTMLInputElement)) continue;

      const o1 = this.#norm(input.value);
      const entriesForSize = this.#entries.filter((e) => this.#norm(e.option1) === o1);
      const hasSizeEntries = entriesForSize.length > 0;
      const exactPackEntry = o2 ? this.#findEntry(o1, o2) : undefined;

      // Size strikethrough:
      // - If a pack is selected and this size has that exact grouped product row, trust that row's availability.
      // - If there is no exact row (this size only exists with other packs in the collection), do NOT scratch
      //   as long as some purchasable row exists for this size — the picker will switch pack via #ensureValidPair.
      // - Previously we treated a missing exact row like sold out, which showed strikes on Medium/Large/Monster
      //   even when those sizes were in stock under a different pack.
      let isAvailable = false;
      if (o2) {
        if (exactPackEntry) {
          isAvailable = Boolean(exactPackEntry.available);
        } else {
          isAvailable = entriesForSize.some((e) => Boolean(e.available));
        }
      } else {
        isAvailable = entriesForSize.some((e) => Boolean(e.available));
      }

      const shouldStrike = hasSizeEntries && !isAvailable;

      // Size options must stay clickable so users can always switch products.
      // Only reflect unavailable visual state; do not disable interaction.
      labelEl.classList.toggle('is-unavailable', shouldStrike);
      this.#toggleUnavailableStrikethrough(labelEl, shouldStrike);
    }
  }

  /**
   * @param {string} handle
   * @param {AbortSignal} [signal]
   */
  async #fetchVariantResource(handle, signal) {
    const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, { signal });
    if (!res.ok) throw new Error('Product JSON fetch failed');
    const product = await res.json();
    const variant = product.variants?.[0];
    if (!variant) throw new Error('No variant on product');

    const imgSrc =
      variant.featured_image?.src ||
      (typeof product.featured_image === 'string' ? product.featured_image : product.featured_image?.src);

    return {
      id: variant.id,
      title: variant.public_title || variant.title || '',
      product_title: product.title || '',
      option1: variant.option1 || '',
      option2: variant.option2 || '',
      available: Boolean(variant.available),
      inventory_management: Boolean(variant.inventory_management),
      sku: variant.sku,
      featured_media: imgSrc ? { preview_image: { src: imgSrc } } : undefined,
    };
  }

  /**
   * @param {string} productUrl
   * @returns {string}
   */
  #buildSectionRequestUrl(productUrl) {
    const base = productUrl.split('?')[0] ?? productUrl;
    const url = new URL(base || window.location.pathname, window.location.origin);
    const sectionId = this.dataset.sectionId;
    if (sectionId) url.searchParams.set('section_id', sectionId);
    const view = getViewParameterValue();
    if (view) url.searchParams.set('view', view);
    return url.toString();
  }

  /** Warm cache for a likely navigation (no-op if already prefetched). */
  #prefetchProductPath(pathname) {
    const path = pathname.startsWith('/') ? pathname : `/${pathname}`;
    if (this.#prefetchedPaths.has(path)) return;
    this.#prefetchedPaths.add(path);
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = path;
    document.head.appendChild(link);
  }

  /** @param {Event} event */
  #onPointerEnterPrefetch = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const label = target.closest('label.variant-option__button-label');
    if (!label || !this.contains(label)) return;
    const input = label.querySelector('input[type="radio"]');
    if (!(input instanceof HTMLInputElement) || input.disabled) return;
    const fieldset = label.closest('fieldset[data-product-group-axis]');
    const axis = fieldset?.getAttribute('data-product-group-axis');
    const o1 = axis === '1' ? this.#norm(input.value) : this.#norm(this.#checkedOpt1()?.value ?? '');
    const o2 = axis === '2' ? this.#norm(input.value) : this.#norm(this.#checkedOpt2()?.value ?? '');
    const entry = this.#findEntry(o1, o2);
    const handle = (entry?.handle ?? '').toString().trim();
    if (!handle) return;

    const pathSegs = window.location.pathname.split('/').filter(Boolean);
    const pi = pathSegs.indexOf('products');
    const currentHandle = pi !== -1 && pathSegs[pi + 1] ? pathSegs[pi + 1] : '';
    if (!currentHandle || handle === currentHandle) return;

    pathSegs[pi + 1] = handle;
    this.#prefetchProductPath(`/${pathSegs.join('/')}`);
  };

  /** @param {Event} event */
  #onChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || event.target.type !== 'radio') return;

    this.#syncOption1Availability();
    this.#syncDisabledPackOptions();
    this.#ensureValidPair();
    this.#syncPackPrices();
    this.#syncOption1Availability();
    this.#syncDisabledPackOptions();

    const entry = this.#findEntry(this.#checkedOpt1()?.value ?? '', this.#checkedOpt2()?.value ?? '');
    if (!entry) return;

    const prevProductId = this.dataset.productId ?? '';
    this.#abort?.abort();
    this.#abort = new AbortController();

    const requestUrl = this.#buildSectionRequestUrl(entry.url);

    const { signal } = this.#abort;
    Promise.all([
      fetch(requestUrl, { signal }).then((r) => {
        if (!r.ok) throw new Error(`Section fetch failed: ${r.status}`);
        return r.text();
      }),
      this.#fetchVariantResource(entry.handle, signal),
    ])
      .then(([htmlText, variantResource]) => {
        const html = new DOMParser().parseFromString(htmlText, 'text/html');
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        // Keep title swap immediate and deterministic for grouped-product switches.
        const sectionId = this.dataset.sectionId;
        const currentSection = sectionId ? document.querySelector(`#shopify-section-${sectionId}`) : document;
        const newSection = sectionId ? html.querySelector(`#shopify-section-${sectionId}`) : html;
        if (currentSection && newSection) {
          // Text block titles use <h1> inside .product-details .text-block (no data-product-main-title).
          const titleSelectors = [
            '[data-product-main-title]',
            '.product-details .text-block h1',
            '.product-details h1',
            '.view-product-title a',
            '.product-details .link',
          ];
          for (const selector of titleSelectors) {
            const currentTitle = currentSection.querySelector(selector);
            const nextTitle = newSection.querySelector(selector);
            if (currentTitle && nextTitle) {
              morph(currentTitle, nextTitle);
            }
          }
        }
        // Final fallback: set heading/link text from product JSON (covers any DOM drift).
        if (variantResource?.product_title && currentSection) {
          const fallbackTitleNode = currentSection.querySelector(
            '[data-product-main-title] h1, [data-product-main-title] h2, [data-product-main-title] h3, [data-product-main-title] h4, [data-product-main-title] h5, [data-product-main-title] h6, [data-product-main-title] p, .product-details .text-block h1, .product-details h1, .view-product-title a'
          );
          if (fallbackTitleNode) {
            fallbackTitleNode.textContent = variantResource.product_title;
          }
        }

        const newProductUrl = entry.url.split('?')[0] ?? entry.url;
        this.dispatchEvent(
          new VariantUpdateEvent(variantResource, this.id || 'product-group-picker', {
            html,
            productId: prevProductId,
            newProduct: { id: String(entry.id), url: newProductUrl },
          })
        );

        this.dataset.productId = String(entry.id);
        this.dataset.productUrl = entry.url.split('?')[0];

        const url = new URL(window.location.href);
        url.pathname = new URL(entry.url, window.location.origin).pathname;
        url.searchParams.set('variant', String(entry.variantId));
        history.replaceState({}, '', url.toString());
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        console.error('[product-group-picker]', err);
      });
  };
}

if (!customElements.get('product-group-picker')) {
  customElements.define('product-group-picker', ProductGroupPicker);
}
